"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Image from "next/image";
import { Phone, MapPinned } from "lucide-react";
import MaskedImage from "@/components/MaskedImage";
import CircleArrow from "@/components/iconComponents/CircleArrow";
import Ticon from "@/public/icon/Ticon.svg";
import { getImageUrl } from "@/lib/utils/image";
import StoreSection from "@/components/stores/StoreSection";
import CatalogButton from "@/components/button/CatalogButton";
import CallButton from "@/components/button/CallButton";

// Lazy load Swiper
let Swiper = null;
let SwiperSlide = null;
let EffectFade = null;

/**
 * StoreHero component displays a featured store with a fade transition.
 * @param {Object} props
 * @param {Array} props.stores - List of store objects
 */
export default function StoreHero({ stores = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swiperLoaded, setSwiperLoaded] = useState(false);
  const swiperRef = useRef(null);

  const handleNext = useCallback(() => swiperRef.current?.slideNext(), []);
  const handlePrev = useCallback(() => swiperRef.current?.slidePrev(), []);

  useEffect(() => {
    // Lazy load Swiper with fade effect
    Promise.all([
      import("swiper/react"),
      import("swiper/modules"),
      import("swiper/css"),
      import("swiper/css/effect-fade")
    ]).then(([swiperReact, swiperModules]) => {
      Swiper = swiperReact.Swiper;
      SwiperSlide = swiperReact.SwiperSlide;
      EffectFade = swiperModules.EffectFade;
      setSwiperLoaded(true);
    }).catch(console.error);
  }, []);

  if (!stores?.length) return null;

  // Fallback before Swiper loads - show first store
  if (!swiperLoaded || !Swiper) {
    const store = stores[0];
    return (
      <div>
        <div className="w-full relative">
          <div className="flex flex-col lg:flex-row justify-between gap-8 lg:h-[500px]">
            <div className="w-full flex flex-col order-2 lg:order-1">
              <div className="h-20 w-full font-dana-light text-mute flex items-center text-lg md:text-2xl gap-3 justify-center lg:justify-end">
                <div className="flex gap-5 mr-4 items-center font-extrabold">
                  <span className="order-2 lg:order-1 font-dana-light">
                    {store.province}
                  </span>
                </div>
              </div>
              <div className="w-full flex-1 flex flex-col justify-between glass-box p-8 md:p-10 text-mute rounded-36 relative min-h-[300px]">
                <div className="flex items-start flex-wrap text-lg lg:text-2xl font-dana-light">
                  <Image src={Ticon} alt="Ticon" width={48} height={48} className="mt-1" aria-hidden="true" />
                  <div className="flex-1 lg:mr-4">
                    <div className="text-white lg:text-mute">
                      <span className="lg:text-mute">{store.city || store.province}</span>
                      <span className="px-1 text-mute">,</span>
                      <span className="text-white font-dana-medium">{store.title}</span>
                    </div>
                    <address className="w-full mt-5 text-base md:text-lg leading-relaxed not-italic">
                      {store.address}
                    </address>
                  </div>
                </div>
                <div className="space-y-7 mt-8 lg:mt-0">
                  <a href={`tel:${store.phone}`} className="flex gap-x-5 items-center hover:text-white transition-colors group">
                    <Phone size={20} className="text-white stroke-[1.5px] group-hover:scale-110 transition-transform" />
                    <span dir="ltr" className="font-dana-regular">{store.phone}</span>
                  </a>
                  {store.latitude && store.longitude && (
                    <a href={`https://www.google.com/maps?q=${store.latitude},${store.longitude}`} target="_blank" rel="noopener noreferrer" className="flex gap-x-5 items-center hover:text-white transition-colors group">
                      <MapPinned size={24} className="text-white stroke-[1.5px] group-hover:scale-110 transition-transform" />
                      <span className="font-dana-regular">مشاهده روی نقشه</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="w-full h-[350px] lg:w-[542px] shrink-0 lg:h-full order-1 lg:order-2">
              <MaskedImage
                src={getImageUrl(store.image) || "/image/hero.png"}
                size="sm"
                alt={store.title || "Store Image"}
                className="w-full h-full object-cover rounded-36"
                priority
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>


      <div className="w-full relative">
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
          className="w-full"
          spaceBetween={32}
          slidesPerView={1}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          modules={[EffectFade]}
        >
          {stores.map((store, index) => (
            <SwiperSlide key={store.id}>
              <div className="flex flex-col lg:flex-row justify-between gap-8 lg:h-[500px]">
                <div className="w-full flex flex-col order-2 lg:order-1">
                  {/* Navigation and Province */}
                  <div className="h-20 w-full font-dana-light text-mute flex items-center text-lg md:text-2xl gap-3 justify-center lg:justify-end">
                    <div className="flex gap-5 mr-4 items-center font-extrabold">
                      <span className="order-2 lg:order-1 font-dana-light">
                        {store.province}
                      </span>
                      <CircleArrow
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        className="disabled:opacity-30 transition-opacity order-1 lg:order-2"
                        aria-label="Previous store"
                      />
                      <CircleArrow
                        onClick={handleNext}
                        disabled={currentIndex === stores.length - 1}
                        className="rotate-180 disabled:opacity-30 transition-opacity order-3 lg:order-3"
                        aria-label="Next store"
                      />
                    </div>
                  </div>

                  {/* Store Information Card */}
                  <div className="w-full flex-1 flex flex-col justify-between glass-box p-8 md:p-10 text-mute rounded-36 relative min-h-[300px]">
                    <div className="flex items-start flex-wrap text-lg lg:text-2xl font-dana-light">
<Image src={Ticon} alt="" width={48} height={48} className="mt-1" aria-hidden="true" />
                      <div className="flex-1 lg:mr-4">
                        <div className="text-white lg:text-mute">
                          <span className="lg:text-mute">
                            {store.city || store.province}
                          </span>
                          <span className="px-1 text-mute">,</span>
                          <span className="text-white font-dana-medium">
                            {store.title}
                          </span>
                        </div>
                        <address className="w-full mt-5 text-base md:text-lg leading-relaxed not-italic">
                          {store.address}
                        </address>
                      </div>
                    </div>

                    <div className="space-y-7 mt-8 lg:mt-0">
                      <a
                        href={`tel:${store.phone}`}
                        className="flex gap-x-5 items-center hover:text-white transition-colors group"
                      >
                        <Phone size={20} className="text-white stroke-[1.5px] group-hover:scale-110 transition-transform" />
                        <span dir="ltr" className="font-dana-regular">
                          {store.phone}
                        </span>
                      </a>

                      {store.latitude && store.longitude && (
                        <a
                          href={`https://www.google.com/maps?q=${store.latitude},${store.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex gap-x-5 items-center hover:text-white transition-colors group"
                        >
                          <MapPinned size={24} className="text-white stroke-[1.5px] group-hover:scale-110 transition-transform" />
                          <span className="font-dana-regular">
                            مشاهده روی نقشه
                          </span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Store Image */}
                <div className="w-full h-[350px] lg:w-[542px] shrink-0 lg:h-full order-1 lg:order-2">
                  <MaskedImage
                    src={getImageUrl(store.image) || "/image/hero.png"}
                    size="sm"
                    alt={store.title || "Store Image"}
                    className="w-full h-full object-cover rounded-36"
                    priority={index === 0}
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}

